import { AxiosError } from "axios";

const INVALID_BASKET_STATUS = "invalid basket status";
const ICCID_MISSING = "iccid missing for basket";
const MNP_DATA_MISSING = "mnp data missing for basket";
const TOTAL_AMOUNT_CHANGED = /total amount for basket.*changed/;
const EMAIL_NOT_VERIFIED = "email not verified";

const collectErrorText = (err: unknown): string => {
  const parts: string[] = [];

  if (typeof err === "string") {
    parts.push(err);
  }

  if (err instanceof Error) {
    parts.push(err.message);
  }

  if (err && typeof err === "object" && "response" in err) {
    const response = (err as AxiosError).response;

    // Compax often returns the full phrase as the body, e.g.
    // `400 Bad Request: ""invalid basket status""` — coerce with `${}` like
    // other frontend-api error checks in this repo.
    if (response?.data !== undefined) {
      parts.push(`${response.data}`);
    }
  }

  try {
    parts.push(JSON.stringify(err));
  } catch {
    // ignore circular / non-serializable values
  }

  return parts.join(" ").toLowerCase();
};

/**
 * Compax returns 400 with "invalid basket status" when a basket can no longer
 * accept options (e.g. already checked out / otherwise closed). The only
 * recovery is to invalidate it and start a new basket.
 */
export const isInvalidBasketStatusError = (err: unknown): boolean =>
  collectErrorText(err).includes(INVALID_BASKET_STATUS);

/**
 * Compax returns 400 `Iccid missing for Basket with ID: …` on checkout when the
 * basket entry has no SIM assigned.
 */
export const isIccidMissingError = (err: unknown): boolean =>
  collectErrorText(err).includes(ICCID_MISSING);

/**
 * Compax returns 400 `MNP Data missing for Basket with ID: …` on checkout when
 * the entry is flagged for number porting but has no porting data.
 */
export const isMnpDataMissingError = (err: unknown): boolean =>
  collectErrorText(err).includes(MNP_DATA_MISSING);

/**
 * Compax returns 409 `Total amount for Basket with ID changed: …` when the
 * basket total differs from the last calculated one. Refreshing the basket
 * resolves it.
 */
export const isTotalAmountChangedError = (err: unknown): boolean =>
  TOTAL_AMOUNT_CHANGED.test(collectErrorText(err));

/**
 * Compax aborts checkout/pay when the customer email is still unverified, e.g.
 * `500 Internal Server Error: ""Process aborted. Email not verified""`.
 * Magic-link login can skip the welcome-screen verification gate.
 */
export const isEmailNotVerifiedError = (err: unknown): boolean =>
  collectErrorText(err).includes(EMAIL_NOT_VERIFIED);
